import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, Zap, Lightbulb, Ban } from "lucide-react";

const COLOR = "#F97316";
const DURATION = 60;

const Skeleton = () => (
  <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-8 space-y-4 animate-pulse">
    <div className="h-10 w-2/3 bg-[#e5e1d5] rounded" />
    <div className="h-6 w-1/3 bg-[#e5e1d5] rounded" />
    <div className="h-4 w-full bg-[#e5e1d5] rounded mt-4" />
    <div className="h-12 w-full bg-[#fef2f2] border-2 border-[#1a1a1a] rounded mt-4" />
  </div>
);

const LogoEn60 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [hint, setHint] = useState("");
  const [hintLoading, setHintLoading] = useState(false);
  const cardRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    setHint("");
    try {
      const res = await api.post("/generate/logo-en-60", {});
      setData(res.data);
      setPhase("ready");
    } catch {
      toast.error("Ups, algo salió mal. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");

  const fetchHint = async () => {
    if (!data) return;
    setHintLoading(true);
    try {
      const d = data.data || {};
      const briefStr = `${d.marca} — ${d.sector} — ${d.sentimiento} — ${d.restriccion}`;
      const res = await api.post("/generate/logo-en-60/hint", { brief: briefStr });
      setHint(res.data.hint || "");
    } catch {
      toast.error("No se pudo generar la pista.");
    } finally {
      setHintLoading(false);
    }
  };

  const d = data?.data || {};

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#F97316] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 08 · nuevo
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            logo en <span className="text-[#F97316]">60s</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            Marca ficticia + sector + restricción extraña. Sketcha el logo antes de que acabe el tiempo.
          </p>
        </div>

        {phase === "idle" && !loading && (
          <button
            data-testid="logo-generate-btn"
            onClick={generate}
            className="press bg-[#F97316] text-white border-4 border-[#1a1a1a] rounded-2xl px-8 py-5 font-display font-black text-2xl shadow-hard-md inline-flex items-center gap-2"
          >
            <Zap className="w-6 h-6" strokeWidth={3} />
            ¡Dame el brief!
          </button>
        )}

        {loading && phase === "idle" && <Skeleton />}

        {phase === "ready" && data && (
          <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-6 sm:p-10 pop-in relative" data-testid="logo-ready">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · logo en 60s
            </div>

            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">la marca</div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <h2 data-testid="logo-marca" className="font-display font-black text-3xl sm:text-5xl tracking-tight">{d.marca}</h2>
              <span className="font-mono text-xs uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-3 py-1 bg-[#dcfce7]">
                {d.sector}
              </span>
            </div>

            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">el reto</div>
            <p className="font-mono text-base sm:text-lg text-[#1a1a1a] mb-6 leading-relaxed">
              {d.sentimiento}
            </p>

            <div className="bg-[#FEF2F2] border-4 border-[#1a1a1a] rounded-2xl p-4 sm:p-5 flex items-start gap-3 mb-8">
              <div className="w-8 h-8 rounded-lg border-2 border-[#7f1d1d] bg-white flex items-center justify-center shrink-0">
                <Ban className="w-4 h-4 text-[#7f1d1d]" strokeWidth={3} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-[#7f1d1d] mb-1">restricción</div>
                <div className="font-mono text-sm sm:text-base font-bold text-[#7f1d1d]">{d.restriccion}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <button
                data-testid="logo-start-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> iniciar 60s
              </button>
              <button
                data-testid="logo-regen-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                otro brief
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="logo-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-logo-en-60-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="logo-download"
              />
            </div>
          </div>
        )}

        {phase === "running" && (
          <div data-testid="logo-running">
            <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-display font-black text-2xl mb-6">
              {d.marca}
            </div>
            <CountdownTimer duration={DURATION} color={COLOR} running format="seconds" warnAt={15} onComplete={finish} />
          </div>
        )}

        {phase === "done" && (
          <>
            <div className="bg-[#F97316] text-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in mb-6" data-testid="logo-done">
              <div className="font-display font-black text-5xl sm:text-7xl mb-4">¡tiempo! ⚡</div>
              <p className="font-mono text-base sm:text-lg mb-8">Comparte tu trazo o pide una pista para la próxima.</p>
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="logo-another-btn"
                  onClick={generate}
                  disabled={loading}
                  className="press inline-flex items-center gap-2 bg-[#1a1a1a] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
                >
                  <RotateCcw className="w-5 h-5" strokeWidth={3} /> otro brief
                </button>
                {data && <ShareButton promptId={data.id} color="#fff" testId="logo-done-share" />}
              </div>
            </div>

            <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-6 sm:p-8" data-testid="logo-hint-box">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl border-4 border-[#1a1a1a] bg-[#FFB000] flex items-center justify-center">
                  <Lightbulb className="w-5 h-5" strokeWidth={3} />
                </div>
                <h3 className="font-display font-black text-xl sm:text-2xl">¿Cómo lo hubieras resuelto?</h3>
              </div>
              {!hint && (
                <button
                  data-testid="logo-hint-btn"
                  onClick={fetchHint}
                  disabled={hintLoading}
                  className="press inline-flex items-center gap-2 bg-[#FFB000] border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm disabled:opacity-60"
                >
                  {hintLoading ? "Pensando..." : "dame una pista"}
                </button>
              )}
              {hint && (
                <p data-testid="logo-hint-text" className="font-mono text-sm sm:text-base text-[#1a1a1a] leading-relaxed bg-[#fff7e6] border-2 border-[#1a1a1a] rounded-xl p-4">
                  💡 {hint}
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </Layout>
  );
};

export default LogoEn60;
